import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/outline";
import axios from "axios";
import { SERVER_URL } from "../config";
import DateSearch from "../components/DateSearch";
import TableHeaderFilter from "../components/TableHeaderfilter";
import ExcelExport from "../components/ExcelExport";
import SearchableDropdown from "../components/SearchableDropdown";
import { FaGift } from 'react-icons/fa';

/* ── status badge ──────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
    const map = {
        present: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        absent: "bg-rose-50 text-rose-700 border border-rose-200",
        leave: "bg-amber-50 text-amber-700 border border-amber-200",
        holiday: "bg-blue-50 text-blue-700 border border-blue-200",
        sunday: "bg-blue-50 text-blue-700 border border-blue-200",
        third_saturday: "bg-blue-50 text-blue-700 border border-blue-200",
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {status}
        </span>
    );
};


/* ── summary stat card ─────────────────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ════════════════════════════════════════════════════════════════════════════ */
const AdminAttendanceOverview = () => {
    const [search, setSearch] = useState("");
    const [attendanceData, setAttendanceData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [employeeIdFilter, setEmployeeIdFilter] = useState([]);
    const [employeeNameFilter, setEmployeeNameFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [hasDateRange, setHasDateRange] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showHolidayModal, setShowHolidayModal] = useState(false);
    const [holidayForm, setHolidayForm] = useState({
        date: "",
        name: "",
        type: "",
        region: "",
    });
    const [regionOptions, setRegionOptions] = useState([]);
    const NON_WORKING_STATUSES = [
    "holiday",
    "sunday",
    "third_saturday",
    "week_off",
    "friday_off"
];

    const fetchRegions = async () => {
        try {
            const res = await axios.post(
                `${SERVER_URL}/Rahul/branchFetch.php`,
                { employee_id: adminEmployeeId }
            );

            if (res.data.status) {
                setRegionOptions(
                    res.data.branches.map(r => ({
                        label: r,
                        value: r
                    }))
                );
            }
        } catch (err) {
            console.error(err);
        }
    };


    let adminEmployeeId = null;
    try {
        const employeeData = localStorage.getItem("employee");
        if (employeeData) {
            const parsed = JSON.parse(employeeData);
            adminEmployeeId = parsed.employee_id || parsed.id || null;
        }
    } catch (e) {
        console.error("Invalid employee data in localStorage");
    }

    /* ---------- map response ---------- */
    const mapAttendanceData = (data) =>
        data.map(item => ({
            ...item,
            date: item.attendance_date || item.date || new Date().toISOString().slice(0, 10),
            checkInTime: item.checkInTime ?? (item.checkin ? item.checkin : "-"),
            checkOutTime: item.checkOutTime ?? (item.checkout ? item.checkout : "-"),
            checkInLocationName: item.checkInLocationName ?? "-",
            checkOutLocationName: item.checkOutLocationName ?? "-",
        }));

    /* ---------- fetch today ---------- */
    const fetchTodayAttendance = async () => {
        setIsLoading(true);
        try {
            const res = await axios.post(`${SERVER_URL}/DataFetch/attendanceAllToday.php`);
            setAttendanceData(res.data.status ? mapAttendanceData(res.data.attendance || []) : []);
        } catch (err) { console.error(err); setAttendanceData([]); }
        finally { setIsLoading(false); }
    };

    /* ---------- fetch date range ---------- */
    const fetchAttendanceByDateRange = async (from, to) => {
        setIsLoading(true);
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/attendanceByDateRange.php`, { start_date: from, end_date: to });
            setAttendanceData(res.data.status ? mapAttendanceData(res.data.records || []) : []);
        } catch (err) { console.error(err); setAttendanceData([]); }
        finally { setIsLoading(false); }
    };

    /* ---------- update status ---------- */
    const updateAttendanceStatus = async (employee_id, attendance_date, status) => {
        const adminId = JSON.parse(localStorage.getItem("employee"))?.employee_id;
        const payload = { employee_id, status, admin_id: adminId };
        if (attendance_date) payload.attendance_date = attendance_date;
        setUpdatingId(employee_id);
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/updateAttendanceStatus.php`, payload);
            if (!res.data.status) {
                alert(res.data.message);
            } else {
                setAttendanceData(prev =>
                    prev.map(row =>
                        row.employee_id === employee_id && row.date === (attendance_date || row.date)
                            ? { ...row, status } : row
                    )
                );
            }
        } catch (err) { console.error(err); }
        finally { setUpdatingId(null); }
    };

    /* ---------- date search handler ---------- */
    const handleDateSearch = (from, to, hasRange) => {
        setFromDate(from || "");
        setToDate(to || "");
        setHasDateRange(!!hasRange);
        hasRange ? fetchAttendanceByDateRange(from, to) : fetchTodayAttendance();
    };

    useEffect(() => {
        fetchTodayAttendance();
        fetchRegions();
    }, []);

    /* ---------- filter options ---------- */
    const employeeIdOptions = useMemo(() => (
        [...new Set(attendanceData.map(r => r.employee_id))].map(id => ({ label: String(id), value: String(id) }))
    ), [attendanceData]);

    const employeeNameOptions = useMemo(() => (
        [...new Set(attendanceData.map(r => r.employee_name))].map(name => ({ label: name, value: name }))
    ), [attendanceData]);

    const statusOptions = useMemo(() => (
        [...new Set(attendanceData.map(r => r.status))].map(s => ({ label: s.toUpperCase(), value: s }))
    ), [attendanceData]);

    /* ---------- filtered data ---------- */
    const filteredData = useMemo(() => {
        const q = search.toLowerCase();
        return attendanceData.filter(row => {
            const matchesSearch = String(row.employee_id || "").toLowerCase().includes(q) || String(row.employee_name || "").toLowerCase().includes(q);
            const matchesEmpId = employeeIdFilter.length === 0 || employeeIdFilter.includes(String(row.employee_id));
            const matchesEmpName = employeeNameFilter.length === 0 || employeeNameFilter.includes(row.employee_name);
            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(row.status);
            return matchesSearch && matchesEmpId && matchesEmpName && matchesStatus;
        });
    }, [attendanceData, search, employeeIdFilter, employeeNameFilter, statusFilter]);

    /* ── SUMMARY STATS ────────────────────────────────────────────────────── */
    const overallStats = useMemo(() => {
        const NON_WORKING_STATUSES = [
            "holiday",
            "sunday",
            "third_saturday",
            "week_off",
            "friday_off"
        ];
        const presentCount = filteredData.filter(r => r.status === "present").length;
        const absentCount = filteredData.filter(r => r.status === "absent").length;
        const leaveCount = filteredData.filter(r => r.status === "leave").length;
        const holidayCount = filteredData.filter(r => NON_WORKING_STATUSES.includes(r.status)).length;
        return { presentCount, absentCount, leaveCount, holidayCount };
    }, [filteredData]);

    const singleEmployeeSelected = useMemo(() => {
        const byName = employeeNameFilter.length === 1;
        const byId = employeeIdFilter.length === 1;
        if (!byName && !byId) return null;

        const empId = byId ? employeeIdFilter[0] : null;
        const empName = byName ? employeeNameFilter[0] : null;

        const rows = attendanceData.filter(r =>
            (!empId || String(r.employee_id) === empId) &&
            (!empName || r.employee_name === empName)
        );

        if (rows.length === 0) return null;
        return rows[0].employee_name || empId;
    }, [employeeIdFilter, employeeNameFilter, attendanceData]);

    const employeeStats = useMemo(() => {
        if (!hasDateRange || !singleEmployeeSelected) return null;

        const NON_WORKING_STATUSES = [
            "holiday",
            "sunday",
            "third_saturday",
            "week_off",
            "friday_off"
        ];

        const empId = employeeIdFilter.length === 1 ? employeeIdFilter[0] : null;
        const empName = employeeNameFilter.length === 1 ? employeeNameFilter[0] : null;

        const rows = attendanceData.filter(r =>
            (!empId || String(r.employee_id) === empId) &&
            (!empName || r.employee_name === empName)
        );

        const totalDays = rows.length;

        const presentDays = rows.filter(
            r => r.status === "present"
        ).length;

        const leaveDays = rows.filter(
            r => r.status === "leave"
        ).length;

        const holidayDays = rows.filter(
            r => NON_WORKING_STATUSES.includes(r.status)
        ).length;

        /*
           Working days should only include days
           where attendance was expected.
        
           Exclude:
           - Leave
           - Holiday
           - Sunday
           - Week Off
           - Friday Off
        */
        const workingDays = rows.filter(
            r =>
                !NON_WORKING_STATUSES.includes(r.status) &&
                r.status !== "leave"
        ).length;

        const absentDays = rows.filter(
            r => r.status === "absent"
        ).length;

        return {
            totalDays,
            presentDays,
            leaveDays,
            holidayDays,
            absentDays,
            workingDays,
            name: singleEmployeeSelected
        };
    }, [hasDateRange, singleEmployeeSelected, attendanceData, employeeIdFilter, employeeNameFilter]);

    /* ---------- action buttons ---------- */
    const getActionButtons = status => {
        if (NON_WORKING_STATUSES.includes(status)) return [];
        if (status === "present") return ["absent", "leave"];
        if (status === "absent") return ["present", "leave"];
        if (status === "leave") return ["present", "absent"];
        return [];
    };

    const ACTION_STYLES = {
        present: "text-emerald-700 border border-emerald-200 hover:bg-emerald-100 bg-emerald-50",
        absent: "text-rose-700 border border-rose-200 hover:bg-rose-100 bg-rose-50",
        leave: "text-amber-700 border border-amber-200 hover:bg-amber-100 bg-amber-50",
    };

    const excelColumns = [
        { header: "Employee ID", key: "employee_id", type: "text" },
        { header: "Employee Name", key: "employee_name", type: "text" },
        { header: "Date", key: "date", type: "text" },
        { header: "Check In", key: "checkInTime", type: "text" },
        { header: "Check In Location", key: "checkInLocationName", type: "text" },
        { header: "Check Out", key: "checkOutTime", type: "text" },
        { header: "Check Out Location", key: "checkOutLocationName", type: "text" },
        { header: "Status", key: "status", type: "text" },
    ];

    const handleHolidaySubmit = async () => {
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/addHoliday.php`, holidayForm);

            if (res.data.status) {
                alert("Holiday added successfully");
                setShowHolidayModal(false);
                setHolidayForm({ date: "", name: "", type: "", region: "" });
                fetchTodayAttendance();
            } else {
                alert(res.data.message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    /* ════════════════════════════════════════════════════════════════════════
       JSX
    ════════════════════════════════════════════════════════════════════════ */
    return (
        <>
            {/* PAGE WRAPPER — same #f0f4f8 background as LeaveApplications */}
            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Subtle top accent line — same gradient as LeaveApplications */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                {/* Same blue accent bar as LeaveApplications */}
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Attendance Overview
                                </h1>
                            </div>
                        </div>
                        <ExcelExport
                            data={filteredData}
                            columns={excelColumns}
                            fileName="Attendance_Report.xlsx"
                            reportTitle="Attendance Report"
                            reportFilters={{ fromDate, toDate }}
                        />
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap w-full">

                            {/* Summary stat pills */}
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                                <StatCard label="Present" value={overallStats.presentCount} colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200" dotClass="bg-emerald-500" />
                                <StatCard label="Absent" value={overallStats.absentCount} colorClass="bg-rose-50 text-rose-700 border border-rose-200" dotClass="bg-rose-500" />
                                <StatCard label="On Leave" value={overallStats.leaveCount} colorClass="bg-amber-50 text-amber-700 border border-amber-200" dotClass="bg-amber-500" />
                                <StatCard label="Holiday" value={overallStats.holidayCount} colorClass="bg-blue-50 text-blue-700 border border-blue-200" dotClass="bg-blue-500" />
                            </div>

                            {/* Right: DateSearch + Search input */}
                            <div className="flex items-center gap-3 ml-auto">
                                <DateSearch onSearch={handleDateSearch} />

                                <input
                                    type="text"
                                    placeholder="Search by Emp ID or Name"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700"
                                />

                                {/* ADD HOLIDAY BUTTON */}
                                <button
                                    onClick={() => setShowHolidayModal(true)}
                                    className="h-10 px-4 rounded-lg text-sm font-semibold text-white transition-all"
                                    style={{
                                        background: "linear-gradient(90deg, #1e40af, #0369a1)",
                                        boxShadow: "0 1px 4px rgba(15,23,42,0.15)"
                                    }}
                                >
                                    + Add Holiday
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── EMPLOYEE-SPECIFIC STATS (date range + single employee selected) ── */}
                    {employeeStats && (
                        <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                            {/* Header */}
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-1 h-5 rounded-full bg-blue-700" />
                                <span className="text-sm font-bold text-slate-800">
                                    {employeeStats.name}
                                </span>
                                <span className="text-xs text-slate-400 ml-1">
                                    — {fromDate} to {toDate}
                                </span>
                            </div>
                            {/* Attendance progress bar */}
                            <div className="rounded-xl px-4 py-3 bg-slate-50 border border-slate-200">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Attendance Rate</span>
                                    <span className="text-xs font-bold text-slate-700">
                                        {employeeStats.workingDays > 0
                                            ? `${Math.round(
                                                (employeeStats.presentDays /
                                                    (employeeStats.presentDays + employeeStats.absentDays)
                                                ) * 100
                                            )}%`
                                            : "100%"}
                                    </span>
                                </div>
                                <div className="w-full h-2 rounded-full overflow-hidden bg-slate-200">
                                    <div
                                        className="h-full rounded-full transition-all duration-700"
                                        style={{
                                            width:
                                                (employeeStats.presentDays + employeeStats.absentDays) > 0
                                                    ? `${Math.round(
                                                        (employeeStats.presentDays /
                                                            (employeeStats.presentDays + employeeStats.absentDays)
                                                        ) * 100
                                                    )}%`
                                                    : "100%",
                                            background: "linear-gradient(90deg, #1e40af, #0369a1)",
                                        }}
                                    />
                                </div>
                                <div className="mt-2">
                                    <span className="text-[10px] text-slate-500">
                                        {employeeStats.presentDays} present ·
                                        {employeeStats.leaveDays} leave ·
                                        {employeeStats.holidayDays} holidays/week-offs ·
                                        {employeeStats.absentDays} absent
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── TABLE — same rounded-xl white card as LeaveApplications ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[520px] att-scrollbar">
                            <table className="min-w-[900px] w-full text-sm">

                                {/* thead — same #f8fafc sticky header */}
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Emp ID"
                                                options={employeeIdOptions}
                                                selectedValues={employeeIdFilter}
                                                onChange={setEmployeeIdFilter}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Name"
                                                options={employeeNameOptions}
                                                selectedValues={employeeNameFilter}
                                                onChange={setEmployeeNameFilter}
                                                fullWidth
                                            />
                                        </th>
                                        {["Date", "Check In", "Check In Location", "Check Out", "Check Out Location"].map(h => (
                                            <th key={h} className="p-3 text-center border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                                            </th>
                                        ))}
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Status"
                                                options={statusOptions}
                                                selectedValues={statusFilter}
                                                onChange={setStatusFilter}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</span>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={9} className="p-8 text-center text-slate-400 text-sm">
                                                <span className="inline-block animate-pulse">Loading…</span>
                                            </td>
                                        </tr>
                                    ) : filteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="p-8 text-center text-slate-400 text-sm">
                                                No attendance data found.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredData.map((row, i) => (
                                            <tr
                                                key={i}
                                                className={`border-t border-slate-100 transition-colors hover:bg-blue-50/60 ${updatingId === row.employee_id ? "opacity-40 pointer-events-none" : ""}`}
                                            >
                                                <td className="p-3 text-slate-500 text-xs font-medium">{row.employee_id}</td>
                                                <td className="p-3 text-slate-800 text-xs font-medium">{row.employee_name}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.date}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.checkInTime}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.checkInLocationName}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.checkOutTime}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.checkOutLocationName}</td>
                                                <td className="p-3 text-center">
                                                    <StatusBadge status={row.status} />
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex gap-2 justify-center flex-wrap">
                                                        {getActionButtons(row.status).map(opt => (
                                                            <button
                                                                key={opt}
                                                                onClick={() => updateAttendanceStatus(row.employee_id, row.date, opt)}
                                                                disabled={updatingId === row.employee_id}
                                                                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${ACTION_STYLES[opt]}`}
                                                            >
                                                                {opt === "present" ? "✓ " : opt === "absent" ? "✕ " : ""}
                                                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                                                            </button>
                                                        ))}
                                                        {getActionButtons(row.status).length === 0 && (
                                                            <span className="text-slate-300 text-xs">—</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
                {showHolidayModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-5"
                            style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>

                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <FaGift className="w-5 h-5 text-blue-600" />
                                    Create Holiday
                                </h2>
                                <button onClick={() => setShowHolidayModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
                            </div>

                            {/* Form */}
                            <div className="flex flex-col gap-3">

                                {/* Date */}
                                <input
                                    type="date"
                                    value={holidayForm.date}
                                    onChange={e => setHolidayForm({ ...holidayForm, date: e.target.value })}
                                    className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                />

                                {/* Name */}
                                <input
                                    type="text"
                                    placeholder="Holiday Name"
                                    value={holidayForm.name}
                                    onChange={e => setHolidayForm({ ...holidayForm, name: e.target.value })}
                                    className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                />

                                {/* Type */}
                                <select
                                    value={holidayForm.type}
                                    onChange={e => setHolidayForm({ ...holidayForm, type: e.target.value })}
                                    className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                >
                                    <option value="">Select Type</option>
                                    <option value="national">National</option>
                                    <option value="regional">Regional</option>
                                    <option value="company">Company</option>
                                </select>

                                {/* Region (Searchable Dropdown) */}
                                <SearchableDropdown
                                    label="Applicable Region"
                                    options={regionOptions}
                                    value={holidayForm.region}
                                    onChange={(val) => setHolidayForm({ ...holidayForm, region: val })}
                                    placeholder="Select Region"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-2 mt-5">
                                <button
                                    onClick={() => setShowHolidayModal(false)}
                                    className="px-4 py-2 rounded-lg text-sm border border-slate-200 text-slate-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleHolidaySubmit}
                                    className="px-4 py-2 rounded-lg text-sm text-white"
                                    style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AdminAttendanceOverview;